import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  Input,
  Typography,
} from "@mui/joy";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "../../lib/api";
import {
  formatForDatetimeLocalInput,
  getOrganizationTimezone,
  parseFromDatetimeLocalInput,
} from "../../lib/timezoneUtils";

export function TaskScheduleFields({
  due,
  allDay,
  onChange,
  assignedTo,
  timezone,
}: {
  timezone?: string;
  due?: string | null;
  allDay?: boolean;
  assignedTo?: number | null;
  onChange: (date: string | null, allDay: boolean) => void;
}) {
  const zone = timezone || getOrganizationTimezone();
  const local = due ? formatForDatetimeLocalInput(new Date(due), zone) : "";
  const [date, time = "09:00"] = local.split("T");
  const save = (day: string, clock = time, dateOnly = !!allDay) =>
    onChange(
      day
        ? parseFromDatetimeLocalInput(
            `${day}T${dateOnly ? "23:59" : clock || "09:00"}`,
            zone,
          ).toISOString()
        : null,
      dateOnly,
    );
  const workload = useQuery({
    queryKey: ["tasks", "workload", assignedTo],
    enabled: !!assignedTo,
    queryFn: async () =>
      (
        await apiClient.get<any>(
          `/api/tasks/statistics/?assigned_to=${assignedTo}&status__in=not_started,in_progress,on_hold`,
        )
      ).data,
  });
  return (
    <Box sx={{ display: "grid", gap: 1 }}>
      <FormControl>
        <FormLabel>Due date · {zone}</FormLabel>
        <Input
          type="date"
          value={date || ""}
          onChange={(e) => save(e.target.value)}
        />
      </FormControl>
      <Checkbox
        label="Date only / all day"
        checked={!!allDay}
        onChange={(e) => save(date || "", time, e.target.checked)}
      />
      {!allDay && (
        <FormControl>
          <FormLabel>Due time (optional)</FormLabel>
          <Input
            type="time"
            value={date ? time : ""}
            disabled={!date}
            onChange={(e) => save(date, e.target.value)}
          />
        </FormControl>
      )}
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        {[0, 1, 7].map((days) => (
          <Button
            key={days}
            size="sm"
            variant="soft"
            onClick={() => {
              const today = formatForDatetimeLocalInput(new Date(), zone).split(
                "T",
              )[0];
              const day = new Date(`${today}T12:00:00Z`);
              day.setUTCDate(day.getUTCDate() + days);
              save(day.toISOString().slice(0, 10));
            }}
          >
            {days === 0 ? "Today" : days === 1 ? "Tomorrow" : "In A Week"}
          </Button>
        ))}
      </Box>
      <Typography level="body-xs">
        {assignedTo
          ? workload.data
            ? `Assignee: ${workload.data.total} open · ${workload.data.overdue} overdue`
            : "Loading assignee workload…"
          : "Unassigned — choose an owner when this is ready for someone to work."}
      </Typography>
    </Box>
  );
}
